<template>
  <div class="region-wrapper inline-block">
    <el-dropdown
      :disabled="switching"
      @command="switchRegion"
      @visible-change="handleDropdownVisible"
    >
      <span class="el-dropdown-link region-trigger">
        <i class="el-icon-location-outline region-icon"></i>
        <span class="region-code">{{ currentRegion || '—' }}</span>
        <span class="trangle"></span>
      </span>
      <el-dropdown-menu slot="dropdown" class="region-dropdown-menu">
        <el-dropdown-item
          v-for="region in availableRegions"
          :key="region.code"
          :command="region.code"
          :disabled="region.code === currentRegion && switchStatus !== 'failed'"
          :class="{
            'is-current-region': region.code === currentRegion
          }"
        >
          <span class="region-menu-badge">{{ region.code }}</span>
          <span class="region-menu-details">
            <span class="region-menu-name">
              {{ region.name || region.code }}
            </span>
          </span>
          <i
            v-if="region.code === currentRegion"
            class="el-icon-check region-menu-check"
          ></i>
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
    <OrganisationSelection
      ref="organisationSelection"
      :organisations="organisations"
      :append-to-body="true"
      @assignPermissionSuccess="finishRegionSwitch"
    />
    <CreateOrganisationDialog
      v-if="showCreateOrganisation"
      ref="createOrganisationDialog"
      :append-to-body="true"
      @createSuccess="handleOrganisationCreated"
      @cancel="cancelRegionSwitch"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import OrganisationSelection from '@/components/signIn/OrganisationSelection.vue'
import CreateOrganisationDialog from '@/components/manage/organisation/CreateOrganisationDialog.vue'

export default {
  components: {
    OrganisationSelection,
    CreateOrganisationDialog
  },
  data() {
    return {
      switching: false,
      showCreateOrganisation: false,
      previousRegion: '',
      workflowLoading: null
    }
  },
  computed: {
    currentRegion() {
      return this.$store.state.region.currentRegion
    },
    availableRegions() {
      return this.$store.state.region.availableRegions
    },
    organisations() {
      return this.$store.getters['user/userOwnedOrganisations']
    },
    switchStatus() {
      return this.$store.state.region.switchStatus
    }
  },
  methods: {
    handleDropdownVisible(visible) {
      if (!visible || this.availableRegions.length) return
      this.switching = true
      this.$store
        .dispatch('region/fetchAvailableRegions')
        .then(() => {
          this.switching = false
        })
        .catch(() => {
          this.switching = false
          this.showSwitchError()
        })
    },
    switchRegion(region) {
      if (region === this.currentRegion && this.switchStatus !== 'failed') {
        return
      }

      const canRestoreExistingPage = this.switchStatus === 'idle'
      if (canRestoreExistingPage) {
        this.previousRegion = this.currentRegion
      }
      this.switching = true
      this.showWorkflowLoading()
      this.$store.commit('region/SET_SWITCH_STATUS', 'updating')
      this.$store
        .dispatch('region/updatePreferredRegion', region)
        .then(() => {
          this.switching = false
          Promise.resolve(Vue.prototype.stopSignalR()).catch(() => null)
          return this.$store.dispatch('region/prepareRegionSwitch')
        })
        .then(this.handleOrganisationResult)
        .catch(() => {
          this.switching = false
          this.closeWorkflowLoading()
          if (this.switchStatus === 'updating') {
            this.$store.commit(
              'region/SET_SWITCH_STATUS',
              canRestoreExistingPage ? 'idle' : 'failed'
            )
          }
          this.showSwitchError()
        })
    },
    handleOrganisationResult(result) {
      const organisations = this.organisations
      this.closeWorkflowLoading(() => {
        this.$nextTick(() => {
          if (organisations.length) {
            this.$refs.organisationSelection.openDialog()
          } else if (result.canCreateOrganisation) {
            this.showCreateOrganisation = true
            this.$nextTick(() => {
              this.$refs.createOrganisationDialog.openDialog()
            })
          } else {
            this.$store.commit('region/SET_SWITCH_STATUS', 'failed')
            this.showNoOrganisationAccess()
          }
        })
      })
    },
    handleOrganisationCreated() {
      this.showWorkflowLoading()
      this.$store
        .dispatch('region/prepareRegionSwitch')
        .then(this.handleOrganisationResult)
        .catch(() => {
          this.closeWorkflowLoading()
          this.showSwitchError()
        })
    },
    cancelRegionSwitch() {
      if (this.previousRegion && this.previousRegion !== this.currentRegion) {
        this.switchRegion(this.previousRegion)
      } else {
        this.$store.commit('region/SET_SWITCH_STATUS', 'failed')
      }
    },
    finishRegionSwitch() {
      this.$store.dispatch('region/completeRegionSwitch')
      window.location.replace('/')
    },
    showWorkflowLoading() {
      this.workflowLoading = this.$loading({
        lock: true,
        text: this.$t('text.loading_organisations'),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.45)'
      })
    },
    closeWorkflowLoading(afterClose) {
      if (!this.workflowLoading) {
        if (afterClose) afterClose()
        return
      }
      const loading = this.workflowLoading
      this.workflowLoading = null
      if (afterClose) loading.$once('after-leave', afterClose)
      loading.close()
    },
    showSwitchError() {
      this.$message.error(this.$t('text.switch_region_failed'))
    },
    showNoOrganisationAccess() {
      this.$alert(
        this.$t('text.no_organisation_access_in_region'),
        this.$t('text.warning'),
        {
          confirmButtonText: this.$t('text.ok').toUpperCase(),
          confirmButtonClass: 'confirm-btn',
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false
        }
      ).then(() => this.cancelRegionSwitch())
    }
  }
}
</script>

<style lang="scss" scoped>
.region-wrapper {
  margin-top: -3px;
  margin-right: 35px;
  color: var(--newThemeTopNavTextColor, #98c1d9);
  cursor: pointer;
  vertical-align: top;

  .el-dropdown,
  .trangle {
    color: inherit;
  }
}

.region-trigger {
  display: inline-flex;
  align-items: center;
  min-width: 58px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(152, 193, 217, 0.45);
  border-radius: 14px;
  background: rgba(152, 193, 217, 0.1);
  box-sizing: border-box;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: rgba(152, 193, 217, 0.8);
    background: rgba(152, 193, 217, 0.2);
  }
}

.region-icon {
  margin-right: 6px;
  font-size: 15px;
}

.region-code {
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
}

.trangle {
  display: inline-block;
  width: auto !important;
  height: auto !important;
  margin-bottom: -2px;
  margin-left: 7px;
  vertical-align: 0.255em;
  visibility: visible !important;
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
  background-color: inherit !important;
  content: '';
}
</style>

<style lang="scss">
.region-dropdown-menu.el-dropdown-menu {
  min-width: 220px;
  padding: 8px;
  border: 1px solid #dfe7ed;
  border-radius: 9px;
  box-shadow: 0 8px 24px rgba(35, 63, 82, 0.16);

  .el-dropdown-menu__item {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 58px;
    padding: 8px 42px 8px 10px;
    border-radius: 7px;
    color: #34495e;
    line-height: normal;
    transition: background-color 0.18s ease;

    & + .el-dropdown-menu__item {
      margin-top: 4px;
    }

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus {
      color: #34495e;
      background: #f1f8fc;
    }

    &.is-current-region {
      color: #34495e;
      background: #f1f8fc;
    }

    &.is-disabled {
      color: #34495e;
      cursor: default;
      opacity: 1;
    }
  }

  .region-menu-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 38px;
    height: 38px;
    margin-right: 11px;
    border-radius: 50%;
    color: #52778d;
    background: #eaf3f8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .is-current-region .region-menu-badge {
    color: #fff;
    background: #6ca9ca;
  }

  .region-menu-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .region-menu-name {
    font-size: 14px;
    font-weight: 600;
  }

  .region-menu-check {
    position: absolute;
    top: 50%;
    right: 14px;
    color: #6ca9ca;
    font-size: 16px;
    font-weight: 700;
    transform: translateY(-50%);
  }
}
</style>
